import React, { useCallback, useState } from "react";
import { Btn, CheckIcon, Modal, Spinner } from "@app/shared/src/components_v2";
import {
    PlannedStep,
    ProgressEvent,
    ProgressStep,
    applyProgress,
    failActive,
    finishedCount,
    planSteps,
    progressFraction,
} from "../../helpers/progressSteps";

/**
 * A modal progress bar for a multi-step save, and the hook that drives it.
 *
 * Shown for the whole of a commit that fans out across several writes — resetting a truck to the
 * fleet default can create endpoints, a zone, a trigger per alert, re-read them and wire them, and
 * on a slow link that is long enough for "Saving…" to look hung. The bar's length is fixed by the
 * plan before the first write, so it fills; each step is named as it runs so the reader can see
 * what the unit is being given.
 *
 * It closes itself on success — the toast already says what was created — and stays open on a
 * failure with the broken step marked and the error underneath, because that is the one moment a
 * director needs to read rather than be told. Escape and the scrim are inert while work is in
 * flight: there is nothing to cancel, and dismissing the view would not stop the writes.
 */

export type ProgressRun = {
    title: string;
    steps: ProgressStep[];
    /** Set once the run has thrown; the dialog then offers Close instead of closing itself. */
    error?: string;
};

export function useProgressSteps() {
    const [run, setRun] = useState<ProgressRun>();

    const begin = useCallback((title: string, planned: PlannedStep[]) => setRun({ title, steps: planSteps(planned) }), []);
    const report = useCallback(
        (event: ProgressEvent) => setRun((r) => (r ? { ...r, steps: applyProgress(r.steps, event) } : r)),
        [],
    );
    const fail = useCallback(
        (message: string) => setRun((r) => (r ? { ...r, steps: failActive(r.steps, message), error: message } : r)),
        [],
    );
    const end = useCallback(() => setRun(undefined), []);

    return { run, begin, report, fail, end };
}

const StepMark: React.FC<{ state: ProgressStep["state"] }> = ({ state }) => {
    switch (state) {
        case "done":
            return <CheckIcon size={14} className="text-[var(--safe)]" />;
        case "active":
            return <Spinner className="h-3.5 w-3.5 text-[var(--accent)]" />;
        case "failed":
            return (
                <span aria-label="Failed" className="text-[13px] font-bold leading-none text-[var(--crit)]">
                    ✕
                </span>
            );
        case "skipped":
            return (
                <span aria-label="Skipped" className="text-[13px] font-bold leading-none text-[var(--muted)]">
                    –
                </span>
            );
        default:
            return <span className="block h-3.5 w-3.5 rounded-full border border-[var(--line-2)]" />;
    }
};

export const ProgressDialog: React.FC<{ run: ProgressRun | undefined; onClose: () => void }> = ({ run, onClose }) => {
    if (!run) return null;

    const percent = Math.round(progressFraction(run.steps) * 100);
    const done = finishedCount(run.steps);
    const total = run.steps.length;
    const stopped = Boolean(run.error);

    return (
        <Modal
            open
            // Inert while running: nothing here can be cancelled, and hiding the bar would not
            // stop the writes behind it.
            onClose={stopped ? onClose : () => undefined}
            title={run.title}
            subtitle={stopped ? `Stopped after ${done} of ${total} step${total === 1 ? "" : "s"}` : `${done} of ${total} step${total === 1 ? "" : "s"}`}
            footer={
                stopped ? (
                    <Btn onClick={onClose}>Close</Btn>
                ) : undefined
            }
        >
            <div
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={percent}
                aria-label={run.title}
                className="h-2 w-full overflow-hidden rounded-full bg-[var(--surface-3)]"
            >
                <div
                    className={`h-full rounded-full transition-[width] duration-300 ease-out ${stopped ? "bg-[var(--crit)]" : "bg-[var(--accent)]"}`}
                    style={{ width: `${percent}%` }}
                />
            </div>

            <ol className="flex flex-col gap-2">
                {run.steps.map((s) => (
                    <li key={s.key} className="flex items-start gap-3 text-[13.5px]">
                        <span className="mt-[3px] flex w-4 shrink-0 items-center justify-center">
                            <StepMark state={s.state} />
                        </span>
                        <span className="min-w-0 flex-1">
                            <span
                                className={
                                    s.state === "pending"
                                        ? "text-[var(--muted)]"
                                        : s.state === "failed"
                                          ? "font-semibold text-[var(--crit)]"
                                          : s.state === "skipped"
                                            ? "text-[var(--muted)] line-through"
                                            : "font-medium"
                                }
                            >
                                {s.label}
                            </span>
                            {s.note && <span className="mt-0.5 block text-[12px] leading-snug text-[var(--muted)]">{s.note}</span>}
                        </span>
                    </li>
                ))}
            </ol>

            {stopped && <p className="text-[12.5px] text-[var(--crit)]">{run.error}</p>}
        </Modal>
    );
};
